import { errorText } from '../errorText'
/**
 * Действия по заявке — закреплённый футер карточки (дровер и /processes/[id]), макет 16.10.2026:
 * одна строка — главное действие (или пара решений) и «⋯ Ещё». Формы (решение с комментарием и
 * сканом, причина отзыва, назначение исполнителя, добавление согласующего, закрытие подбора,
 * exit-интервью) открываются листом поверх футера: футер не прыгает и не выдавливает тело карточки.
 *
 * Доступность считается как раньше — taskActions + зеркала серверных правил, ОДНА копия таблиц на
 * дровер и страницу; что главное, а что в меню, решает planActions (processCard.ts). `onOpen` даёт пункт «Открыть на всю
 * страницу» (только дровер). `onClose` выполняется после терминального действия.
 */
import { useEffect, useState } from 'react'
import { Platform, useWindowDimensions } from 'react-native'
import { Input, ScrollView, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../../i18n'
import { palette } from '../../../theme/palette'
import { tone } from '../../../theme/tones'
import { Avatar, Backdrop, Button, FormInput, Icon, IconButton, OptionSelect, PressCard } from '../../../ui'
import { pressable } from '../../../ui/primitives'
import type { EdoDecision, EdoStage, EdoType, Profile, RequestCard, TaskCandidates } from '../../../lib/types'
import { stageActions } from '../../../lib/edo-labels'
import { useMe } from '../../users/useUsers'
import { useUserSearch } from '../../users/useUserSearch'
import {
  useAccrueVacation, useAddApprover, useAssignTask, useCancelRequest, useClaimTask, useRemindRequest,
  useTaskCandidates, useUnclaimTask,
} from '../useEdo'
import { approvalSheetHtml, printApprovalSheet } from '../approvalSheet'
import { type ProcVM, STAGE_ACT, addApproverAccess, taskActions } from '../processVM'
import { type ActionKey, planActions } from '../processCard'
import { docUrl, useCopyLink } from '../useCopyLink'
import { CloseRecruitmentForm } from './CloseRecruitmentForm'
import { DecisionPanel, decisionLabel } from './DecisionPanel'
import { ExitInterviewForm } from './ExitInterviewForm'
import { HireActions } from './HireActions'
import { TerminationActions } from './TerminationActions'
import { ItWorksRework } from '../itWorks/ItWorksActions'
import { ItWorksWorkflow } from '../itWorks/ItWorksWorkflow'

/**
 * Decisions allowed for a stage. Берём единый STAGE_ACTIONS (зеркало бэкендового ALLOWED_DECISIONS,
 * покрыт тестами). Закрытие подбора (CLOSE) идёт НЕ через generic decide, а отдельной формой с
 * обязательным файлом-резюме → решений не рисуем.
 */
// Exit-интервью увольнения тоже своей формой (ExitInterviewForm): результат обязателен.
const decisionsFor = (type: EdoType | undefined, stage: EdoStage): EdoDecision[] =>
  stage === 'CLOSE' || stage === 'EXIT_INTERVIEW' ? [] : stageActions(type, stage)

/**
 * Формы-документы в теле карточки: черновик/приказ увольнения, приём и «подготовить приём» из подбора,
 * IT-работы — черновик, решения по своим строкам модулей (есть строки в canDecideLines) и «сумма
 * изменена» финальному утверждающему.
 */
export function ProcessWorkflow({ detail, onDone }: { detail?: RequestCard; onDone: () => void }) {
  if (!detail) return null
  return (
    <>
      {(detail.type === 'TERMINATION' || detail.type === 'TERMINATION_EMPLOYER') && <TerminationActions key={detail.id} card={detail} onDone={onDone} />}
      {(detail.type === 'HIRE' || (detail.type === 'RECRUITMENT' && detail.status === 'CLOSED')) && <HireActions card={detail} onDone={onDone} />}
      <ItWorksWorkflow card={detail} onDone={onDone} />
    </>
  )
}

/** Решение «вперёд по маршруту» — кнопка primary; отказ, доработка, отзыв и служебное — обычные. */
const POSITIVE = new Set<ActionKey>(['claim', 'close', 'exit', 'accrue', 'remind', 'decide:approve', 'decide:sign', 'decide:resubmit'])

export function ProcessActions({
  p,
  detail,
  onClose,
  onOpen,
  maxWidth,
}: {
  p: ProcVM
  detail?: RequestCard
  onClose?: () => void
  onOpen?: () => void
  /** Страница: содержимое футера по ширине колонки документа (сам футер — во всю ширину). */
  maxWidth?: number
}) {
  const { t, l, lang } = useApp()
  const { data: me } = useMe()
  const done = onClose ?? (() => {})

  // Действия по групповой задаче считаем из ЖИВОЙ карточки (openTask), чтобы кнопки были
  // актуальны после чужого claim, а не по снапшоту VM.
  const openTask = detail?.tasks?.find((tk) => String(tk.status) === 'OPEN')
  const { canClaim, canUnclaim, assignedToOther } = taskActions({
    openTask,
    meId: me?.id,
    initiatorId: detail?.initiator?.id,
    // Подача «от имени» (роль proxy): задача доработки назначена на сотрудника, но закрывает её подавший.
    submittedById: detail?.submittedBy?.id,
    // Заместитель на время отпуска решает личные задачи замещаемого (сервер считает actingFor).
    actingFor: detail?.actingFor,
    // Взять групповую задачу может только носитель роли, не заместитель (сервер считает canClaim).
    claimAllowed: detail?.canClaim,
    canDecide: p.canDecide,
  })
  const claimTaskId = openTask?.id ?? p.taskId
  // Кандидаты для назначения тянем лишь для открытой ГРУППОВОЙ задачи; бэк отдаёт canAssign
  // (лид/admin) — не-лидам список пуст, пикер не показываем.
  const { data: candData } = useTaskCandidates(claimTaskId, !!openTask?.candidateGroup)
  const canAssign = !!candData?.canAssign
  // Решать можно, пока задачу не взял ДРУГОЙ член группы (иначе бэк вернёт 403 «назначена другому»).
  const decisions = p.canDecide && p.taskId && p.stage && !assignedToOther ? decisionsFor(detail?.type, p.stage).filter(d => !(detail?.type === 'HIRE' && p.stage === 'REWORK' && d === 'resubmit')) : []

  // ── Подбор персонала (ТЗ-2026-011) ──
  const isRecruitment = detail?.type === 'RECRUITMENT'
  // Живая карточка ещё грузится: isRecruitment/needComment/статус считаются из detail, до ответа
  // GET /edo/requests/:id «Согласовать» была бы активна без обяз. комментария (бэк вернёт 400),
  // а кнопка отмены мигала бы на терминальных заявках — блокируем действия до загрузки.
  const detailPending = !!p.live && !!p.reqId && !detail
  // Обяз. комментарий для рук./Рук.СП/ГД (бэк также enforce'ит; блокируем кнопку заранее).
  const needComment = isRecruitment && (p.stage === 'MANAGER' || p.stage === 'SUPERVISOR' || p.stage === 'CEO')
  const closeStage = openTask?.stage === 'CLOSE' && !!p.canDecide // ОИ/HR на шаге закрытия
  // Увольнение: Exit-интервью проводит HR, на которого назначена задача (тот, кто проверял заявку).
  const exitInterviewStage =
    detail?.type === 'TERMINATION' && openTask?.stage === 'EXIT_INTERVIEW' && !!p.canDecide && !assignedToOther
  // Командировка v1.2 §2.1: кадровик (HR) обязан вложить скан подписанного приказа — без него бэк
  // вернёт 400, поэтому блокируем кнопку заранее.
  const orderStage = openTask?.stage === 'ORDER' && !!p.canDecide
  /**
   * Договор (ТЗ-2026-015): «без скана заявка не финализируется». С 23.09.2026 скан подписанного
   * экземпляра и срок действия вносит АВТОР на отдельном шаге SIGNED_COPY — последние в маршрутах
   * руководители юрлиц сканы не загружают. Зеркало серверного requiresScanOnDecision; источник
   * правды — бэк, он всё равно ответит 400.
   */
  const contractFinal = detail?.type === 'CONTRACT' && openTask?.stage === 'SIGNED_COPY' && !!p.canDecide
  const needScan = orderStage || contractFinal
  // Заявление на отпуск, шаг HR: кадровик может поправить замещающего прямо при согласовании.
  const hrDeputy = detail?.type === 'VACATION' && openTask?.stage === 'HR' && !!p.canDecide
  /**
   * Правка документа на доработке (К1). Инициатор возвращает не тот же документ, а исправленный:
   * поля payload + новая редакция вложением уходят ВМЕСТЕ с «Отправить снова».
   */
  const reworkEdit =
    openTask?.stage === 'REWORK' && !!p.canDecide &&
    (detail?.type === 'CONTRACT' || detail?.type === 'FREEFORM' || detail?.type === 'TERMINATION' || detail?.type === 'TERMINATION_EMPLOYER')
  // Отзыв заявки: право считает сервер (canCancel карточки — то же правило, что у POST /cancel):
  // инициатор или HR компании заявки / admin, пока заявка не ушла в 1С; подбор — §1.5. Роль hr
  // в токене общая на весь портал, а бэк скоупит её компанией — поэтому не зеркалим, а читаем флаг.
  // На доработке у инициатора уже есть решение «Отменить» по задаче — второй кнопки не надо.
  // Увольнение: сервер разрешает отзыв только сотруднику и только до решения CEO.
  const canCancel = !!detail?.canCancel && !decisions.includes('cancel')
  const showRemind = !!(p.live && p.canRemind && p.mine && p.reqId)
  // Лист согласования (печать/PDF) — всем, кто видит карточку; только веб: печатает браузер.
  const canPrint = Platform.OS === 'web' && !!detail
  const addAccess = addApproverAccess(detail, me?.id)

  const { primary, menu } = planActions({
    decisions,
    claim: canClaim,
    unclaim: canUnclaim,
    assign: canAssign && !!candData,
    close: closeStage && !!detail,
    exit: exitInterviewStage && !!detail && !!p.taskId,
    accrue: !!detail?.canAccrue,
    remind: decisions.length === 0 && showRemind,
    print: canPrint,
    addApprover: addAccess.can && !!detail,
    copyLink: !!p.live,
    openPage: !!onOpen,
    cancel: canCancel && !!detail,
  })
  // IT-работы, этап модулей: решение — панелью строк в теле карточки, в футере кнопок нет.
  const linesHint = !primary.length && !!detail?.itWorks?.canDecideLines?.length
    ? t({ ru: 'Решение — по строкам модулей выше', en: 'Decide on the module lines above', az: 'Qərar — yuxarıdakı modul sətirləri üzrə' })
    : ''

  const [menuOpen, setMenuOpen] = useState(false)
  const [sheet, setSheet] = useState<ActionKey | null>(null)
  const claim = useClaimTask()
  const unclaim = useUnclaimTask()
  const accrue = useAccrueVacation()
  const remind = useRemindRequest()
  const { copy, copied } = useCopyLink(() => docUrl(p.reqId ?? p.id))
  const { height } = useWindowDimensions()

  // Esc закрывает меню и лист. Веб: onKeyDown Tamagui до DOM не доносит — слушаем document.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined' || (!menuOpen && !sheet)) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setMenuOpen(false)
      setSheet(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [menuOpen, sheet])

  const finish = () => {
    setSheet(null)
    done()
  }

  const meta = (k: ActionKey): { label: string; icon?: string } => {
    if (k.startsWith('decide:')) return { label: t(decisionLabel(k.slice(7) as EdoDecision, p.stage, detail?.type)) }
    switch (k) {
      case 'claim':
        return { label: claim.isPending ? t({ ru: 'Берём…', en: 'Claiming…' }) : t({ ru: 'Взять в работу', en: 'Claim task' }), icon: 'user-plus' }
      case 'unclaim':
        return { label: unclaim.isPending ? t({ ru: 'Возвращаем…', en: 'Returning…' }) : t({ ru: 'Вернуть в группу', en: 'Return to group' }), icon: 'user-minus' }
      case 'assign':
        return { label: openTask?.assigneeId ? t({ ru: 'Переназначить исполнителя', en: 'Reassign' }) : t({ ru: 'Назначить исполнителя', en: 'Assign to member' }), icon: 'user-plus' }
      case 'close':
        return { label: t({ ru: 'Закрыть — кандидат найден', en: 'Close — candidate hired' }), icon: 'check' }
      case 'exit':
        return { label: t({ ru: 'Exit-интервью', en: 'Exit interview', az: 'Exit-müsahibə' }), icon: 'message-square' }
      case 'accrue':
        return { label: accrue.isPending ? t({ ru: 'Начисление…', en: 'Accruing…' }) : t({ ru: 'Начислить отпускные', en: 'Accrue vacation pay' }), icon: 'wallet' }
      case 'remind':
        return { label: remind.isSuccess ? t({ ru: 'Напоминание отправлено', en: 'Reminder sent' }) : l('proc.remind'), icon: 'bell' }
      case 'print':
        return { label: t({ ru: 'Лист согласования', en: 'Approval sheet', az: 'Razılaşdırma vərəqi' }), icon: 'printer' }
      case 'addApprover':
        return { label: t({ ru: 'Добавить согласующего', en: 'Add approver', az: 'Razılaşdıran əlavə et' }), icon: 'user-plus' }
      case 'copyLink':
        return { label: l(copied ? 'proc.linkCopied' : 'proc.copyLink'), icon: copied ? 'check' : 'link' }
      case 'openPage':
        return { label: l('card.openPage'), icon: 'external-link' }
      default: // cancel
        return { label: isRecruitment ? t({ ru: 'Отменить заявку', en: 'Cancel request' }) : t({ ru: 'Отозвать заявку', en: 'Withdraw request' }), icon: 'x' }
    }
  }

  const run = (k: ActionKey) => {
    // «Скопировать ссылку» меню не закрывает — в нём же видно «Ссылка скопирована».
    if (k === 'copyLink') return void copy()
    setMenuOpen(false)
    if (k === 'claim') { if (claimTaskId) claim.mutate(claimTaskId) }
    else if (k === 'unclaim') { if (claimTaskId) unclaim.mutate(claimTaskId) }
    else if (k === 'accrue') { if (detail) accrue.mutate({ id: detail.id }) }
    else if (k === 'remind') { if (p.reqId) remind.mutate(p.reqId) }
    else if (k === 'print') { if (detail) printApprovalSheet(approvalSheetHtml(detail, { lang, printedBy: me?.fullName ?? '' })) }
    else if (k === 'openPage') onOpen?.()
    else setSheet(k)
  }

  const sheetBody = (k: ActionKey) => {
    // IT-работы: переотправка с доработки — своя форма вместо общей панели решения. «Отменить» (отзыв) — общее.
    if (k === 'decide:resubmit' && detail?.type === 'IT_WORKS' && p.taskId) {
      return <ItWorksRework card={detail} taskId={p.taskId} onDone={finish} onCancel={() => setSheet(null)} />
    }
    if (k.startsWith('decide:')) {
      return (
        <DecisionPanel
          taskId={p.taskId}
          stage={p.stage}
          detail={detail}
          decisions={[k.slice(7) as EdoDecision]}
          needComment={!!needComment}
          needScan={!!needScan}
          contractFinal={!!contractFinal}
          hrDeputy={hrDeputy}
          reworkEdit={!!reworkEdit}
          detailPending={detailPending}
          onDone={finish}
        />
      )
    }
    if (!detail) return null
    if (k === 'close') return <CloseRecruitmentForm requestId={detail.id} onDone={finish} />
    if (k === 'exit' && p.taskId) return <ExitInterviewForm taskId={p.taskId} requestId={detail.id} onDone={finish} />
    if (k === 'cancel') return <CancelForm requestId={detail.id} onBack={() => setSheet(null)} onDone={finish} />
    if (k === 'assign' && candData) return <AssignList taskId={claimTaskId} data={candData} onDone={() => setSheet(null)} />
    if (k === 'addApprover') return <AddApproverForm card={detail} beforeFinal={addAccess.beforeFinal} onBack={() => setSheet(null)} />
    return null
  }

  // Заголовок листа: у решения и форм шага — название шага (кнопка подтверждения уже называет действие).
  const sheetStage = openTask?.stage ?? p.stage
  const sheetTitle = (k: ActionKey) =>
    (k.startsWith('decide:') || k === 'close' || k === 'exit') && sheetStage ? t(STAGE_ACT[sheetStage]) : meta(k).label

  const error = [claim.error, unclaim.error, accrue.error, remind.error].find((e) => e instanceof Error)

  return (
    // minHeight — высота строки: лист открывается поверх, и тело карточки над футером не прыгает.
    <YStack position="relative" zIndex={1} minHeight={72} borderTopWidth={1} borderColor="$borderSecondary" backgroundColor="$background" paddingHorizontal={20} paddingVertical={14} alignItems="center">
      {!sheet && (
        <YStack width="100%" maxWidth={maxWidth} gap={8}>
          {/* Подсказка — только тому, кто сам мог взять задачу; инициатор и наблюдатели видели её на любом назначенном шаге. */}
          {assignedToOther && p.canDecide && (
            <Text fontSize={12} color="$muted">
              {t({ ru: 'Задачу уже взял другой сотрудник', en: 'Already claimed by another member' })}
            </Text>
          )}
          {primary.length || menu.length ? (
            // Три решения на телефоне в ряд не влезают — «На доработку» уходит на второй ряд, не сжимаясь.
            <XStack gap={10} alignItems="center" flexWrap="wrap">
              {linesHint ? <Text flex={1} fontSize={13} color="$muted">{linesHint}</Text> : null}
              {primary.map((k) => {
                const m = meta(k)
                const positive = POSITIVE.has(k)
                const pending = (k === 'claim' && claim.isPending) || (k === 'unclaim' && unclaim.isPending) || (k === 'accrue' && accrue.isPending) || (k === 'remind' && remind.isPending)
                return (
                  <Button
                    key={k}
                    flex={1}
                    minWidth={120}
                    height={44}
                    variant={positive ? 'primary' : 'default'}
                    // решения ждут живую карточку: до ответа нет обяз. комментария и статуса (см. detailPending)
                    disabled={pending || (k.startsWith('decide:') && detailPending)}
                    opacity={pending || (k.startsWith('decide:') && detailPending) ? 0.6 : 1}
                    icon={m.icon ? <Icon name={m.icon} size={15} color={positive ? '$onPrimary' : '$muted'} /> : undefined}
                    onPress={() => run(k)}
                  >
                    {m.label}
                  </Button>
                )
              })}
              {menu.length ? (
                <IconButton
                  width={44}
                  height={44}
                  borderWidth={1}
                  borderColor="$borderColor"
                  aria-label={l('card.more')}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onPress={() => setMenuOpen(!menuOpen)}
                >
                  <Icon name="ellipsis" size={18} color="$color" />
                </IconButton>
              ) : null}
            </XStack>
          ) : (
            <Text fontSize={13} color="$muted">
              {linesHint || t({ ru: 'Нет доступных действий по документу', en: 'No actions available' })}
            </Text>
          )}
          {error ? <Text fontSize={12} color={tone('red').text}>{t(errorText(error))}</Text> : null}
        </YStack>
      )}

      {menuOpen && !sheet ? (
        <>
          <Backdrop onPress={() => setMenuOpen(false)} />
          <YStack
            role="menu"
            aria-label={l('card.more')}
            position="absolute"
            bottom="100%"
            right={20}
            marginBottom={8}
            width={280}
            maxWidth="90%"
            zIndex={50}
            padding={6}
            borderRadius={10}
            borderWidth={1}
            borderColor="$borderSecondary"
            backgroundColor="$bgElevated"
            shadowColor={palette.shadow}
            shadowOpacity={0.18}
            shadowRadius={20}
            shadowOffset={{ width: 0, height: 8 }}
          >
            {menu.map((k) => {
              const m = meta(k)
              const danger = k === 'cancel'
              return (
                <XStack
                  key={k}
                  {...pressable}
                  role="menuitem"
                  onPress={() => run(k)}
                  cursor="pointer"
                  alignItems="center"
                  gap={10}
                  minHeight={44}
                  paddingHorizontal={12}
                  borderRadius={7}
                  hoverStyle={{ backgroundColor: '$backgroundHover' }}
                >
                  {m.icon ? <Icon name={m.icon} size={15} color={danger ? tone('red').text : '$muted'} /> : null}
                  <Text fontSize={14} color={danger ? tone('red').text : '$color'}>
                    {m.label}
                  </Text>
                </XStack>
              )
            })}
          </YStack>
        </>
      ) : null}

      {sheet ? (
        <YStack
          role="dialog"
          aria-label={sheetTitle(sheet)}
          position="absolute"
          left={0}
          right={0}
          bottom={0}
          zIndex={60}
          maxHeight={Math.round(height * 0.75)}
          backgroundColor="$background"
          borderTopLeftRadius={14}
          borderTopRightRadius={14}
          borderTopWidth={1}
          borderColor="$borderSecondary"
          shadowColor={palette.shadow}
          shadowOpacity={0.18}
          shadowRadius={24}
          shadowOffset={{ width: 0, height: -8 }}
          alignItems="center"
        >
          <XStack width="100%" maxWidth={maxWidth} alignItems="center" justifyContent="space-between" gap={10} paddingLeft={20} paddingRight={12} paddingTop={10}>
            <Text fontSize={15} fontWeight="700" color="$color" flexShrink={1}>
              {sheetTitle(sheet)}
            </Text>
            <IconButton width={44} height={44} aria-label={l('card.close')} onPress={() => setSheet(null)}>
              <Icon name="x" size={18} color="$muted" />
            </IconButton>
          </XStack>
          <ScrollView width="100%" flexShrink={1}>
            <YStack width="100%" maxWidth={maxWidth} alignSelf="center" gap={10} paddingHorizontal={20} paddingBottom={18}>
              {sheetBody(sheet)}
            </YStack>
          </ScrollView>
        </YStack>
      ) : null}
    </YStack>
  )
}

/** Push-назначение: лид группы / admin назначает конкретного исполнителя (или переназначает). */
function AssignList({ taskId, data, onDone }: { taskId?: string; data: TaskCandidates; onDone: () => void }) {
  const { t } = useApp()
  const assign = useAssignTask()
  return (
    <YStack gap={6}>
      {data.candidates.length === 0 && (
        <Text fontSize={12} color="$muted">
          {t({ ru: 'Нет доступных исполнителей', en: 'No members available' })}
        </Text>
      )}
      {data.candidates.map((c) => {
        const current = c.id === data.currentAssigneeId
        return (
          <Button
            key={c.id}
            variant="default"
            block
            height={44}
            disabled={current || assign.isPending}
            opacity={current || assign.isPending ? 0.5 : 1}
            onPress={() => taskId && assign.mutate({ taskId, userId: c.id }, { onSuccess: onDone })}
          >
            {c.fullName}
            {current ? t({ ru: ' · текущий', en: ' · current', az: ' · cari' }) : ''}
          </Button>
        )
      })}
      {assign.error instanceof Error && (
        <Text fontSize={12} color={tone('red').text}>{t(errorText(assign.error))}</Text>
      )}
    </YStack>
  )
}

/** Отзыв заявки: обязательная причина → подтверждение. */
function CancelForm({ requestId, onBack, onDone }: { requestId: string; onBack: () => void; onDone: () => void }) {
  const { t } = useApp()
  const cancelReq = useCancelRequest()
  const [cancelReason, setCancelReason] = useState('')
  return (
    <YStack gap={8}>
      <Input
        value={cancelReason}
        onChangeText={setCancelReason}
        placeholder={t({ ru: 'Причина отмены (обязательно)', en: 'Cancellation reason (required)' })}
        height={44} fontSize={14} color="$color" backgroundColor="$bgLayout" borderWidth={1}
        borderColor="$borderColor" borderRadius={7} paddingHorizontal={11}
        placeholderTextColor="$tertiary" focusStyle={{ borderColor: '$primary' }}
      />
      <XStack gap={10}>
        <Button flex={1} height={44} onPress={onBack}>
          {t({ ru: 'Назад', en: 'Back' })}
        </Button>
        <Button
          flex={1}
          height={44}
          variant="primary"
          opacity={cancelReason.trim() && !cancelReq.isPending ? 1 : 0.5}
          onPress={() => {
            if (!cancelReason.trim()) return
            cancelReq.mutate({ id: requestId, comment: cancelReason.trim() }, { onSuccess: onDone })
          }}
        >
          {cancelReq.isPending ? t({ ru: 'Отмена…', en: 'Cancelling…' }) : t({ ru: 'Подтвердить отмену', en: 'Confirm cancel' })}
        </Button>
      </XStack>
      {cancelReq.error instanceof Error && (
        <Text fontSize={12} color={tone('red').text}>{t(errorText(cancelReq.error))}</Text>
      )}
    </YStack>
  )
}

/** Добавить согласующего в идущий документ: поиск сотрудника → место в очереди → подтверждение. */
function AddApproverForm({ card, beforeFinal, onBack }: { card: RequestCard; beforeFinal: boolean; onBack: () => void }) {
  const { t } = useApp()
  const add = useAddApprover()
  const { q, setQ, debounced, hits } = useUserSearch(card.type === 'CONTRACT' ? 'all' : undefined)
  const [picked, setPicked] = useState<Profile | null>(null)
  const [position, setPosition] = useState<'next' | 'end'>('next')
  // Параллельный круг: места в очереди нет — человек входит в текущий круг.
  const parallel = (card.payload as { parallel?: boolean }).parallel === true
  const taken = new Set([
    card.initiator.id,
    ...(card.route ?? []).flatMap((r) => (r.person ? [r.person.id] : [])),
    ...(card.observers ?? []).map((o) => o.id),
    ...((card.payload as { approvers?: string[] }).approvers ?? []),
  ])
  const found = (hits ?? []).filter((p) => !taken.has(p.id)).slice(0, 8)

  return (
    <YStack gap={8}>
      {picked ? (
        <XStack alignItems="center" gap={10}>
          <Avatar name={picked.fullName} uri={picked.avatarUrl} size={28} />
          <YStack flex={1}>
            <Text fontSize={13} color="$color">{picked.fullName}</Text>
            {!!picked.jobTitle && <Text fontSize={11} color="$muted">{picked.jobTitle}</Text>}
          </YStack>
          <IconButton onPress={() => setPicked(null)}>
            <Icon name="x" size={15} color="$muted" />
          </IconButton>
        </XStack>
      ) : (
        <>
          <FormInput
            value={q}
            onChangeText={setQ}
            placeholder={t({ ru: 'Поиск сотрудника (от 2 символов)', en: 'Search employee (2+ chars)', az: 'Əməkdaş axtarışı (2 simvoldan)' })}
          />
          {found.map((p) => (
            <PressCard key={p.id} padding={9} onPress={() => { setPicked(p); setQ('') }}>
              <XStack alignItems="center" gap={10}>
                <Avatar name={p.fullName} uri={p.avatarUrl} size={28} />
                <YStack flex={1}>
                  <Text fontSize={13} color="$color">{p.fullName}</Text>
                  {!!p.jobTitle && <Text fontSize={11} color="$muted">{p.jobTitle}</Text>}
                </YStack>
                <Icon name="plus" size={15} color="$primary" />
              </XStack>
            </PressCard>
          ))}
          {debounced.length >= 2 && hits && found.length === 0 && (
            <Text fontSize={12} color="$muted">
              {t({ ru: 'Никого не найдено', en: 'No one found', az: 'Heç kim tapılmadı' })}
            </Text>
          )}
        </>
      )}
      {!parallel && (
        <OptionSelect
          options={[
            { value: 'next', label: t({ ru: 'Следующим', en: 'Next', az: 'Növbəti' }) },
            {
              value: 'end',
              label: beforeFinal
                ? t({ ru: 'Перед финальным', en: 'Before the final approver', az: 'Sonuncudan əvvəl' })
                : t({ ru: 'В конец маршрута', en: 'At the end', az: 'Marşrutun sonuna' }),
            },
          ]}
          value={[position]}
          onChange={(next) => setPosition(next[0] === 'end' ? 'end' : 'next')}
        />
      )}
      {beforeFinal && !parallel && (
        <Text fontSize={11} color="$muted">
          {t({
            ru: 'Обязательные шаги не сдвигаются: добавленный встанет после них и до финального согласующего.',
            en: 'Mandatory steps stay in place: the new approver goes after them and before the final approver.',
            az: 'Məcburi mərhələlər yerində qalır: əlavə edilən onlardan sonra və sonuncu razılaşdırandan əvvəl yerləşəcək.',
          })}
        </Text>
      )}
      <XStack gap={10}>
        <Button flex={1} height={44} onPress={onBack}>
          {t({ ru: 'Назад', en: 'Back', az: 'Geri' })}
        </Button>
        <Button
          flex={1}
          height={44}
          variant="primary"
          disabled={!picked || add.isPending}
          opacity={picked && !add.isPending ? 1 : 0.5}
          onPress={() => {
            if (!picked || add.isPending) return
            add.mutate(
              { id: card.id, userId: picked.id, position: parallel ? 'end' : position },
              { onSuccess: onBack },
            )
          }}
        >
          {add.isPending
            ? t({ ru: 'Добавляем…', en: 'Adding…', az: 'Əlavə edilir…' })
            : t({ ru: 'Добавить', en: 'Add', az: 'Əlavə et' })}
        </Button>
      </XStack>
      {add.error instanceof Error && (
        <Text fontSize={12} color={tone('red').text}>{t(errorText(add.error))}</Text>
      )}
    </YStack>
  )
}
